import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import './index.css';
import App from './App';
import { msalConfig } from './config/authConfig';
import { appConfig } from './config/appConfig';

// Create root element reference
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Show loading state
root.render(
  <React.StrictMode>
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '100vh',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>⚙️</div>
        <div>Initializing...</div>
      </div>
    </div>
  </React.StrictMode>
);

// Initialize MSAL and render app
const initializeApp = async () => {
  // Create MSAL instance
  const msalInstance = new PublicClientApplication(msalConfig);
  
  // Initialize MSAL — this also processes any pending redirect response (handleRedirectPromise)
  await msalInstance.initialize();

  if (appConfig.enableAuthentication) {
    // Explicitly process the redirect response so the account is set before React renders.
    // initialize() runs handleRedirectPromise internally but does not expose the result;
    // calling it again is a no-op (returns cached result) and lets us capture the account.
    try {
      const redirectResult = await msalInstance.handleRedirectPromise();
      if (redirectResult?.account) {
        msalInstance.setActiveAccount(redirectResult.account);
      }
    } catch (e) {
      // Redirect errors (e.g. access_denied) — clear state so the user can try again
      console.error('Redirect auth error:', e);
    }

    // Fallback: set active account from any cached session
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  // Listen for sign-in events (covers loginPopup fallback paths)
  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      msalInstance.setActiveAccount(payload.account);
    }
  });

  // Render app after MSAL is fully initialized
  root.render(
    <React.StrictMode>
      {appConfig.enableAuthentication ? (
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      ) : (
        <App />
      )}
    </React.StrictMode>
  );
};

// Start the app
initializeApp().catch(error => {
  console.error('Failed to initialize MSAL:', error);
  // Show error state
  root.render(
    <React.StrictMode>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: '#dc2626'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
            Failed to Initialize
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {error.message || 'An error occurred while starting the application'}
          </div>
        </div>
      </div>
    </React.StrictMode>
  );
});
